import React, { useEffect, useRef, useMemo } from 'react';
import { VectorMap } from 'react-jvectormap';
import ApexCharts from 'apexcharts';
import { View } from 'tamagui';
import { Platform } from 'react-native';

// ==================================
// Reusable Widget Chart Component
// ==================================
/**
 * A small, reusable area chart for widgets.
 * @param {object} props - The component props.
 * @param {Array<number>} props.seriesData - The data points for the series.
 * @param {string} props.chartColor - The primary color for the chart gradient and line.
 */
export const WidgetChart = ({ seriesData, chartColor }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const options = {
      series: [{ name: 'series1', data: seriesData }],
      chart: {
        type: 'area',
        width: 80,
        height: 42,
        sparkline: { enabled: true },
        toolbar: { show: false },
      },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: 2, colors: [chartColor] },
      fill: {
        type: 'gradient',
        colors: [chartColor],
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.5,
          gradientToColors: [`${chartColor}00`],
          inverseColors: false,
          opacityFrom: 0.75,
          opacityTo: 0.3,
          stops: [0, 100],
        },
      },
      markers: {
        colors: [chartColor],
        strokeWidth: 2,
        size: 0,
        hover: { size: 8 },
      },
      xaxis: { labels: { show: false }, tooltip: { enabled: false } },
      yaxis: { labels: { show: false } },
    };

    const chart = new ApexCharts(chartRef.current, options);
    chart.render();
    return () => chart.destroy();
  }, [seriesData, chartColor]);

  return <div ref={chartRef} />;
};


// ==================================
// Reusable Area Chart Component
// ==================================
/**
 * A reusable area chart component.
 * @param {object} props - The component props.
 * @param {Array<object>} props.seriesData - Data for the chart series. E.g. [{ name: 'This Day', data: [4, 18, 13] }]
 * @param {Array<string>} props.categories - Categories for the x-axis.
 * @param {string} [props.chartColor='#487fff'] - The primary color for the chart.
 * @param {number} [props.height=162] - The height of the chart.
 */
export const AreaChart = ({ 
  seriesData, 
  data, 
  categories, 
  chartColor = '#487fff', 
  color,
  fillColor,
  height = 162 
}) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    // Support both old seriesData format and new data format
    const finalColor = color || chartColor;
    const finalFillColor = fillColor || (finalColor + '20');
    
    let chartData;
    if (data && Array.isArray(data)) {
      // New format: [{ x: 'date', y: value }]
      chartData = [{ name: 'series1', data: data.map(item => ({ x: item.x, y: item.y })) }];
    } else if (seriesData) {
      // Old format: direct series data
      chartData = seriesData;
    } else {
      return;
    }

    const options = {
      series: chartData,
      chart: {
        type: 'area',
        height: height,
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: 2, colors: [finalColor] },
      fill: {
        type: 'gradient',
        colors: [finalColor],
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.5,
          gradientToColors: [finalFillColor],
          opacityFrom: 0.6,
          opacityTo: 0.3,
          stops: [0, 100],
        },
      },
      markers: {
        colors: [finalColor],
        strokeWidth: 3,
        size: 0,
        hover: { size: 6 },
      },
      xaxis: {
        type: data ? 'category' : 'category',
        categories: categories,
        labels: { 
          style: { fontSize: "12px" },
          rotate: data ? -45 : 0
        },
      },
      yaxis: { 
        labels: { 
          show: true,
          style: { fontSize: "12px" }
        } 
      },
      grid: {
        borderColor: '#f1f1f1',
        strokeDashArray: 3
      },
      tooltip: {
        shared: true,
        intersect: false
      }
    };

    const chart = new ApexCharts(chartRef.current, options);
    chart.render();
    return () => chart.destroy();
  }, [seriesData, categories, chartColor, height]);

  return <div ref={chartRef} />;
};


// ==================================
// Reusable Bar Chart Component
// ==================================
/**
 * A reusable bar chart component using ApexCharts.
 * @param {object} props - The component props.
 * @param {Array<object>} props.seriesData - The data for the chart series. E.g., [{ x: 'Jan', y: 85000 }]
 * @param {string} [props.seriesName="Sales"] - The name for the data series.
 * @param {Array<string>} props.categories - The categories for the x-axis.
 * @param {number} [props.height=310] - The height of the chart in pixels.
 * @param {Array<string>} [props.colors=['#487FFF']] - The gradient colors for the bars.
 */
export const BarChart = ({
  seriesData,
  seriesName = "Sales",
  categories,
  height = 310,
  colors = ['#487FFF']
}) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const prevDataRef = useRef(null);

  // Memoize series data to prevent unnecessary re-renders
  const memoizedSeriesData = useMemo(() => {
    if (!seriesData || !Array.isArray(seriesData)) return [];
    return seriesData;
  }, [JSON.stringify(seriesData)]);

  // Memoize categories to prevent unnecessary re-renders
  const memoizedCategories = useMemo(() => {
    if (!categories || !Array.isArray(categories)) return [];
    return categories;
  }, [JSON.stringify(categories)]);

  useEffect(() => {
    if (!chartRef.current) return;

    // Check if data actually changed to prevent infinite loops
    const currentDataKey = JSON.stringify({ seriesData: memoizedSeriesData, categories: memoizedCategories, seriesName, height, colors });
    if (prevDataRef.current === currentDataKey && chartInstanceRef.current) {
      return; // Data hasn't changed, skip re-render
    }
    prevDataRef.current = currentDataKey;

    // Destroy existing chart if it exists
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }

    const options = {
      series: [{ name: seriesName, data: memoizedSeriesData }],
      chart: { 
        type: 'bar', 
        height: height, 
        toolbar: { show: false },
        events: {
          // Prevent chart from triggering re-renders
          dataPointSelection: () => {},
          updated: () => {}
        }
      },
      plotOptions: {
        bar: { borderRadius: 4, horizontal: false, columnWidth: '23%', endingShape: 'rounded' }
      },
      dataLabels: { enabled: false },
      fill: {
        type: 'gradient',
        colors: colors,
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.5,
          gradientToColors: colors,
          opacityFrom: 1,
          opacityTo: 1,
          stops: [0, 100],
        },
      },
      grid: { show: true, borderColor: '#D1D5DB', strokeDashArray: 4, position: 'back' },
      xaxis: { type: 'category', categories: memoizedCategories },
      yaxis: {
        labels: {
          formatter: (value) => (value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value),
        },
      },
      tooltip: {
        y: {
          formatter: (value) => (value >= 1000 ? `${value / 1000}k` : value),
        },
      },
    };

    const chart = new ApexCharts(chartRef.current, options);
    chartInstanceRef.current = chart;
    chart.render();
    
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [memoizedSeriesData, memoizedCategories, seriesName, height, colors]);

  return <div ref={chartRef} />;
};


// ==================================
// Reusable Donut Chart Component
// ==================================
/**
 * A reusable donut chart component.
 * @param {object} props - The component props.
 * @param {Array<number>} props.series - The data series. E.g., [500, 500, 500]
 * @param {Array<string>} props.labels - The labels for each series item. E.g., ['Active', 'New', 'Total']
 * @param {Array<string>} [props.colors=['#45B369', '#FF9F29', '#487FFF']] - Colors for the segments.
 * @param {number} [props.height=300] - The height of the chart.
 */
export const DonutChart = ({
  series,
  labels,
  chartTitle = "User Distribution",
  colors = ['#45B369', '#FF9F29', '#487FFF'],
  height = 300
}) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const options = {
      series: series,
      labels: labels,
      colors: colors,
      chart: { type: 'donut', height: height },
      stroke: { width: 0 },
      dataLabels: { enabled: false },
      legend: { show: false },
      plotOptions: {
        pie: {
          donut: {
            size: '70%',
            labels: {
              show: true,
              total: { show: true, showAlways: true, label: chartTitle },
            },
          },
        },
      },
    };

    const chart = new ApexCharts(chartRef.current, options);
    chart.render();
    return () => chart.destroy();
  }, [series, labels, colors, height]);

  return <div ref={chartRef} />;
};


// ==================================
// Reusable Multi-Series Bar Chart
// ==================================
/**
 * A reusable multi-series bar chart for comparisons.
 * @param {object} props - The component props.
 * @param {Array<object>} props.series - The data series. E.g., [{ name: 'Net Profit', data: [44, 100] }]
 * @param {Array<string>} props.categories - The categories for the x-axis.
 * @param {Array<string>} [props.colors=['#45B369', '#144bd6', '#FF9F29']] - Colors for each series.
 * @param {number} [props.height=350] - The height of the chart.
 */
export const MultiSeriesBarChart = ({
  series,
  categories,
  colors = ['#45B369', '#144bd6', '#FF9F29'],
  height = 350
}) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const options = {
      series: series,
      colors: colors,
      chart: { type: 'bar', height: height, toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: 8 } },
      dataLabels: { enabled: false },
      stroke: { show: true, width: 0, colors: ['transparent'] },
      xaxis: { categories: categories },
      grid: { show: true, borderColor: '#D1D5DB', strokeDashArray: 4 },
      fill: { opacity: 1 },
      legend: { show: false },
    };

    const chart = new ApexCharts(chartRef.current, options);
    chart.render();
    return () => chart.destroy();
  }, [series, categories, colors, height]);

  return <div ref={chartRef} />;
};


// ==================================
// Map Component (Unchanged)
// ==================================
export const MapComponent = ({ data, markers }) => {
  const fillRegions = () => {
    if (!data) return { 'GB': '#D1D5DB' };
    const regions = {};
    try {
      data.forEach(item => {
        if (item.code && item.color) {
          regions[item.code] = item.color || '#D1D5DB';
        }
      });
    } catch (error) {
      console.error("Error processing data for map regions:", error);
      return { 'GB': '#D1D5DB' };
    }
    return regions;
  };

  const mapOptions = {
    map: 'world_mill',
    backgroundColor: 'transparent',
    regionStyle: { initial: { fill: '#D1D5DB' } },
    markers: markers || [],
    markerStyle: { initial: { fill: '#fff', stroke: '#000', r: 5 } },
    series: { regions: [{ values: fillRegions(), attribute: 'fill' }] },
    zoomOnScroll: false,
  };

  return Platform.OS === "web" && (
    <View style={{ width: '100%', height: "100%", flex: 1 }}>
      <VectorMap
        containerStyle={{ flex: 1 }}
        {...mapOptions}
      />
    </View>
  );
};

// ==================================
// Reusable Line Chart Component
// ==================================
/**
 * A reusable line chart component with prediction support.
 * @param {object} props - The component props.
 * @param {Array<object>} props.data - The data points. E.g., [{ x: 'Jan', y: 100 }]
 * @param {string} [props.color='#487FFF'] - The line color.
 * @param {number} [props.height=250] - The height of the chart.
 * @param {number} [props.predictedStartIndex] - Index where predictions start.
 */
export const LineChart = ({ 
  data, 
  color = '#487FFF', 
  height = 250,
  predictedStartIndex 
}) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !data) return;

    const historicalData = predictedStartIndex 
      ? data.slice(0, predictedStartIndex)
      : data;
    
    const predictedData = predictedStartIndex 
      ? data.slice(predictedStartIndex - 1) // Include one overlap point
      : [];

    const series = [
      {
        name: 'Historical',
        data: historicalData.map(item => ({ x: item.x, y: item.y })),
        color: color
      }
    ];

    if (predictedData.length > 0) {
      series.push({
        name: 'Predicted',
        data: predictedData.map(item => ({ x: item.x, y: item.y })),
        color: color + '80'
      });
    }

    const options = {
      series,
      chart: {
        type: 'line',
        height,
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      dataLabels: { enabled: false },
      stroke: {
        curve: 'smooth',
        width: [3, 2],
        dashArray: [0, 5] // Dashed line for predictions
      },
      markers: {
        size: [4, 3],
        hover: { size: 6 }
      },
      xaxis: {
        type: 'category',
        labels: { 
          style: { fontSize: '12px' },
          rotate: -45
        }
      },
      yaxis: {
        labels: { 
          style: { fontSize: '12px' }
        }
      },
      legend: {
        show: predictedData.length > 0,
        position: 'top',
        horizontalAlign: 'right'
      },
      grid: {
        borderColor: '#f1f1f1',
        strokeDashArray: 3
      },
      tooltip: {
        shared: true,
        intersect: false
      }
    };

    const chart = new ApexCharts(chartRef.current, options);
    chart.render();
    return () => chart.destroy();
  }, [data, color, height, predictedStartIndex]);

  return <div ref={chartRef} />;
};
